import Middleman from '~/js/lib/middleman';
import {
  PurchasedWithCurrentCartBuilder,
  PurchasedWithMultipleProductsBuilder,
  PopularProductsBuilder,
  DataValueFactory,
  PurchasedWithProductBuilder,
  RecentlyViewedProductsBuilder,
  PersonalProductRecommendationBuilder,
  SimilarProductsProductBuilder,
  ValueSelectorFactory, ProductsViewedAfterViewingProductBuilder,
} from '@relewise/client';

function fetchCart() {
  return fetch('/cart?view=data').then((res) => res.json());
}

/**
 * General DOCS overview
 * @filters https://github.com/Relewise/relewise-sdk-javascript/tree/f8b3cc234ef10d27cedcd70caa7649cefb6efe6e
 */

class Recommendations {
  constructor({ displayedAtLocation = 'Home' }) {
    this.middleman = new Middleman();

    this.displayedAtLocation = displayedAtLocation;

    this.defaultProductProps = {
      displayName: true,
      pricing: true,
      AllVariants: true,
      dataKeys: [
        'Vendor',
        `${window.Shopify.shop}_ShopifyHandle`,
        `${window.Shopify.shop}_ShopifyProductId`,
        'Tags',
        'InStock',
        'IsAvailable',
      ],
    };

    this.defaultVariantProps = {
      displayName: true,
      pricing: true,
      dataKeys: [
        `${window.Shopify.shop}_ImageUrls`,
        `${window.Shopify.shop}_ShopifyVariantId`,
        'InventoryQuantity',
        'IsAvailable',
      ],
    };
  }

  /**
   * Helps build the instance. Basically for DRY-reasons.
   * @param {BuilderType} ClassType
   * @returns a build
   */
  buildInstanceHelper(ClassType) {
    return (
      new ClassType({
        ...this.middleman.settings,
        ...{ displayedAtLocation: this.displayedAtLocation },
      })

        /**
         * Pick which Product props to return.
         * Data can be found in my.relewise.com > Entities > Products
         */
        .setSelectedProductProperties(this.defaultProductProps)

        /**
         * Pick which Product props to return.
         * Data can be found in my.relewise.com > Entities > Products
         */
        .setSelectedVariantProperties(this.defaultVariantProps)

        .setNumberOfRecommendations(12)

        .filters((filter) => filter
          .addProductDataFilter('ShopifyDomains', (condition) => condition.addContainsCondition(
            DataValueFactory.string(window.Shopify.shop),
          ))

          /**
           * Only return variants from current shop/domain
           */
          .addVariantDataFilter('ShopifyDomains', (condition) => condition.addContainsCondition(
            DataValueFactory.string(window.Shopify.shop),
          ))

          /**
           * Remove products marked as "Auktion". Second param is "negated"
           */
          .addProductDataFilter('Vendor', (condition) => condition.addEqualsCondition('Auktion', true))

          /**
           * Only show products with inventory
           */
          .addProductDataFilter('IsAvailable', (condition) => condition.addEqualsCondition(true)))
    );
  }

  getCartUpsells() {
    if (this.middleman.isAnonymous()) {
      return this.getAnonymousCartUpsells();
    }
    return this.getTrackedCartUpsells();
  }

  async getAnonymousCartUpsells() {
    const builder = this.buildInstanceHelper(
      PurchasedWithMultipleProductsBuilder,
    );
    const cart = await fetchCart();

    const products = cart.items.map((item) => ({
      productId: item.relewise_id,
      variantId: item.sku,
    }));

    builder.addProducts(products);

    return this.middleman.recommender.recommendPurchasedWithMultipleProducts(
      builder.build(),
    );
  }

  async getTrackedCartUpsells() {
    const builder = this.buildInstanceHelper(PurchasedWithCurrentCartBuilder);
    return this.middleman.recommender.recommendPurchasedWithCurrentCart(
      builder.build(),
    );
  }

  async getPurchasedWithProductRecoms({ productId, categoryId, vendor }) {
    if (!productId || productId === '') {
      // eslint-disable-next-line no-console
      console.warn(
        'Expected productId to be set for PurchasedWithProductRequest. Instead using PopularProductsRequest',
      );
      return this.getPopularProducts();
    }

    let builder;

    if (categoryId && vendor) {
      builder = this.buildInstanceHelper(PurchasedWithProductBuilder)
        .product({ productId })
        .relevanceModifiers((modifier) => (
          modifier.addProductCategoryIdRelevanceModifier(categoryId, 'ImmediateParentOrItsParent', 2)
            .addProductDataRelevanceModifier('Vendor', (condition) => condition.addEqualsCondition(vendor, true), ValueSelectorFactory.dataDoubleSelector('Vendor'))
        ));
    } else {
      builder = this.buildInstanceHelper(PurchasedWithProductBuilder)
        .product({ productId });
    }

    return this.middleman.recommender.recommendPurchasedWithProduct(
      builder.build(),
    );
  }

  async getPopularProducts() {
    const builder = this.buildInstanceHelper(
      PopularProductsBuilder,
    ).sinceMinutesAgo(7 * 24 * 60);

    return this.middleman.recommender.recommendPopularProducts(builder.build());
  }

  async getRecentlyViewedProducts() {
    if (this.middleman.isAnonymous()) {
      // eslint-disable-next-line no-console
      console.warn(
        'Expected RecentlyViewedProducts, but user is anonymous. Instead using PopularProductsRequest',
      );
      return this.getPopularProducts();
    }

    const builder = this.buildInstanceHelper(RecentlyViewedProductsBuilder);

    return this.middleman.recommender.recentlyViewedProducts(builder.build());
  }

  async getPersonalProductRecoms() {
    const builder = this.buildInstanceHelper(
      PersonalProductRecommendationBuilder,
    );

    return this.middleman.recommender.recommendPersonalProducts(
      builder.build(),
    );
  }

  async getProductsViewedAfterViewingProduct(productId) {
    const builder = this.buildInstanceHelper(
      ProductsViewedAfterViewingProductBuilder,
    ).product({ productId });

    return this.middleman.recommender.recommendProductsViewedAfterViewingProduct(
      builder.build(),
    );
  }

  async getSimilarProducts({ productId, vendor }) {
    if (!productId || productId === '') {
      // eslint-disable-next-line no-console
      console.warn(
        'Expected productId to be set for SimilarProductsRequest. Instead using PopularProductsRequest',
      );
      return this.getPopularProducts();
    }

    // Use SimilarProductsProductBuilder for actual similar products
    let builder = this.buildInstanceHelper(SimilarProductsProductBuilder)
      .product({ productId })
      .setNumberOfRecommendations(12);

    // Add vendor filter if provided to only show products from the same vendor
    if (vendor && vendor !== '') {
      // Create a new builder with additional vendor filter
      builder = new SimilarProductsProductBuilder({
        ...this.middleman.settings,
        ...{ displayedAtLocation: this.displayedAtLocation },
      })
        .setSelectedProductProperties(this.defaultProductProps)
        .setSelectedVariantProperties(this.defaultVariantProps)
        .setNumberOfRecommendations(12)
        .product({ productId })
        .filters((filter) => filter
          .addProductDataFilter('ShopifyDomains', (condition) => condition.addContainsCondition(
            DataValueFactory.string(window.Shopify.shop),
          ))
          .addVariantDataFilter('ShopifyDomains', (condition) => condition.addContainsCondition(
            DataValueFactory.string(window.Shopify.shop),
          ))
          .addProductDataFilter('IsAvailable', (condition) => condition.addEqualsCondition(true))
          .addProductDataFilter('Vendor', (condition) => condition.addEqualsCondition(vendor)));
    }

    return this.middleman.recommender.recommendSimilarProducts(builder.build());
  }
}

export default Recommendations;
